/**
 * Global Vehicle State Context
 * Manages selected vehicle state across all Used Car pages with persistence and URL synchronization
 */

import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLocalStorageState } from '@toolpad/core/useLocalStorageState'
import type { VehicleSearchResult } from '../api/lenderSolution'

// Simple structured logger for frontend
const logger = {
  info: (message: string, data?: any) => {
    console.log(`[VehicleStateContext] ${message}`, data || '')
  },
  warn: (message: string, data?: any) => {
    console.warn(`[VehicleStateContext] ${message}`, data || '')
  },
  error: (message: string, data?: any) => {
    console.error(`[VehicleStateContext] ${message}`, data || '')
  }
}

// Context interface
export interface VehicleStateContextType {
  activeVehicle: VehicleSearchResult | null
  vehicleHistory: VehicleSearchResult[]
  setActiveVehicle: (vehicle: VehicleSearchResult) => void
  clearActiveVehicle: () => void
  isLoading: boolean
  error: string | null
}

// Create context
const VehicleStateContext = createContext<VehicleStateContextType | undefined>(undefined)

// Provider props
interface VehicleStateProviderProps {
  children: ReactNode
}

// Validate vehicle object
const isValidVehicle = (vehicle: any): vehicle is VehicleSearchResult => {
  return vehicle && 
         typeof vehicle === 'object' &&
         typeof vehicle.uvc === 'string' &&
         typeof vehicle.year === 'number' &&
         typeof vehicle.make === 'string' &&
         typeof vehicle.model === 'string'
}

// Provider component
export const VehicleStateProvider: React.FC<VehicleStateProviderProps> = ({ children }) => {
  // Use Toolpad's useLocalStorageState for automatic persistence
  const [activeVehicle, setActiveVehicleState] = useLocalStorageState<VehicleSearchResult | null>(
    'bb-active-vehicle',
    null,
    {
      codec: {
        parse: (value) => {
          try {
            const parsed = JSON.parse(value)
            const isValid = isValidVehicle(parsed)
            if (isValid) {
              logger.info('Active vehicle restored from localStorage', { 
                vehicle: `${parsed.year} ${parsed.make} ${parsed.model}`,
                uvc: parsed.uvc 
              })
            }
            return isValid ? parsed : null
          } catch (error) {
            logger.warn('Failed to parse active vehicle from localStorage', { error })
            return null
          }
        },
        stringify: (value) => {
          if (value) {
            logger.info('Active vehicle saved to localStorage', { 
              vehicle: `${value.year} ${value.make} ${value.model}`,
              uvc: value.uvc 
            })
          }
          return JSON.stringify(value)
        }
      }
    }
  )
  
  const [vehicleHistory, setVehicleHistoryState] = useLocalStorageState<VehicleSearchResult[]>(
    'bb-vehicle-history',
    [],
    {
      codec: {
        parse: (value) => {
          try {
            const parsed = JSON.parse(value)
            const validHistory = Array.isArray(parsed) ? parsed.filter(isValidVehicle).slice(0, 10) : []
            if (validHistory.length > 0) {
              logger.info('Vehicle history restored from localStorage', { count: validHistory.length })
            }
            return validHistory
          } catch (error) {
            logger.warn('Failed to parse vehicle history from localStorage', { error })
            return []
          }
        },
        stringify: (value) => {
          if (value.length > 0) {
            logger.info('Vehicle history saved to localStorage', { count: value.length })
          }
          return JSON.stringify(value)
        }
      }
    }
  )
  
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const navigate = useNavigate()
  const location = useLocation()

  // Restore from URL parameters on route change
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const uvcParam = searchParams.get('uvc')
    
    if (uvcParam && (!activeVehicle || activeVehicle.uvc !== uvcParam)) {
      // TODO: Fetch vehicle details by UVC from API
      // For now, we'll just log the intent
      logger.info('URL contains vehicle UVC parameter', { uvc: uvcParam })
    }
  }, [location.search, activeVehicle])

  // Sync URL when active vehicle changes (localStorage is handled automatically by useLocalStorageState)
  useEffect(() => {
    if (activeVehicle) {
      updateURLWithVehicle(activeVehicle)
    } else {
      removeVehicleFromURL()
    }
  }, [activeVehicle])

  // Update URL with vehicle UVC
  const updateURLWithVehicle = (vehicle: VehicleSearchResult): void => {
    try {
      const searchParams = new URLSearchParams(location.search)
      searchParams.set('uvc', vehicle.uvc)
      
      const newURL = `${location.pathname}?${searchParams.toString()}`
      
      // Use replace to avoid creating history entries for every vehicle selection
      window.history.replaceState(null, '', newURL)
    } catch (error) {
      logger.warn('Failed to update URL with vehicle', { uvc: vehicle.uvc, error: error instanceof Error ? error.message : String(error) })
    }
  }

  // Remove vehicle from URL
  const removeVehicleFromURL = (): void => {
    try {
      const searchParams = new URLSearchParams(location.search)
      searchParams.delete('uvc')
      
      const newURL = searchParams.toString() 
        ? `${location.pathname}?${searchParams.toString()}`
        : location.pathname
      
      window.history.replaceState(null, '', newURL)
    } catch (error) {
      logger.warn('Failed to remove vehicle from URL', { error: error instanceof Error ? error.message : String(error) })
    }
  }

  // Add or update vehicle in history (maintain last 10, avoid duplicates)
  const updateVehicleHistory = (vehicle: VehicleSearchResult): VehicleSearchResult[] => {
    const currentHistory = vehicleHistory || []
    const newHistory = currentHistory.filter(v => v.uvc !== vehicle.uvc)
    newHistory.unshift(vehicle)
    return newHistory.slice(0, 10) // Keep only last 10
  }

  // Set active vehicle and update history with scroll preservation
  const setActiveVehicle = useCallback((vehicle: VehicleSearchResult) => {
    if (!isValidVehicle(vehicle)) {
      logger.error('Invalid vehicle object provided to setActiveVehicle', { vehicle })
      setError('Invalid vehicle data')
      return
    }

    try {
      // Preserve current scroll position
      const currentScrollY = window.scrollY
      const currentScrollX = window.scrollX
      
      setError(null)
      setActiveVehicleState(vehicle)
      setVehicleHistoryState(prev => updateVehicleHistory(vehicle))
      
      // Restore scroll position after state update
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        // Double-check the scroll position hasn't been changed by something else
        if (Math.abs(window.scrollY - currentScrollY) > 10) {
          window.scrollTo(currentScrollX, currentScrollY)
        }
      })
      
      logger.info('Active vehicle updated with scroll preservation', { 
        uvc: vehicle.uvc, 
        description: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
        scrollY: currentScrollY
      })
    } catch (error) {
      logger.error('Failed to set active vehicle', { error: error instanceof Error ? error.message : String(error) })
      setError('Failed to select vehicle')
    }
  }, [vehicleHistory])

  // Clear active vehicle with scroll preservation
  const clearActiveVehicle = useCallback(() => {
    try {
      // Preserve current scroll position
      const currentScrollY = window.scrollY
      const currentScrollX = window.scrollX
      
      setActiveVehicleState(null)
      setError(null)
      
      // Restore scroll position after state update
      requestAnimationFrame(() => {
        if (Math.abs(window.scrollY - currentScrollY) > 10) {
          window.scrollTo(currentScrollX, currentScrollY)
        }
      })
      
      logger.info('Active vehicle cleared with scroll preservation', { scrollY: currentScrollY })
    } catch (error) {
      logger.error('Failed to clear active vehicle', { error: error instanceof Error ? error.message : String(error) })
    }
  }, [])

  // Context value
  const contextValue: VehicleStateContextType = {
    activeVehicle,
    vehicleHistory: vehicleHistory || [],
    setActiveVehicle,
    clearActiveVehicle,
    isLoading,
    error
  }

  return (
    <VehicleStateContext.Provider value={contextValue}>
      {children}
    </VehicleStateContext.Provider>
  )
}

// Custom hook to use vehicle state context
export const useGlobalVehicleState = (): VehicleStateContextType => {
  const context = useContext(VehicleStateContext)
  
  if (context === undefined) {
    throw new Error('useGlobalVehicleState must be used within a VehicleStateProvider')
  }
  
  return context
}

// Export context for advanced usage
export { VehicleStateContext }